import { useEffect, useState, type ReactNode } from 'react';
import { btcValue, burnProgress, ratioToBps, useTokenLiquidity } from './TokenLiquidityInfo';
import { AlertTriangle, CheckCircle2, Coins, Flame, Lock, TicketCheck, Unlock } from 'lucide-react';
import type { PrimaryTokenView } from '../lib/lookup-types';
import { api } from '../lib/api';
import { formatDate, formatToken, shortenAddress } from '../lib/format';
import { atomicToDecimal } from '../state/AppDataContext';

/*
 * rules/rule14.txt (Part 5) - the member's Primary Token: identity ID + T, one per ID, with its
 * Era and ratios, 365-day freeze, lifecycle status, redemption eligibility and burn status. Used
 * in the lookup, the admin explorer and the member's own token page.
 */
/** rules/rule15.txt s.4 - the API's live CDTT read for one ID (see apps/api/src/cdtt/cdtt.ts). */
export interface CdttView {
  configured: boolean;
  reason?: string;
  bdId: string;
  tokenAddress: string | null;
  providerAddress: string | null;
  wallet: string | null;
  balanceAtomic: string;
  creditedAtomic: string;
  totalSupplyAtomic: string;
  tokenValueAtomic: string;
  quoteCdttAtomic: string;
  cdttPerBtcbAtomic: string;
  parityLive?: boolean;
  cdtPerUsdAtomic?: string;
  multiplierBps: number;
}

export function useCdttView(bdId: string | null, refreshKey = 0): CdttView | null {
  const [view, setView] = useState<CdttView | null>(null);
  useEffect(() => {
    let cancelled = false;
    setView(null);
    if (!bdId) return;
    api<CdttView>(`/api/public/cdtt/${encodeURIComponent(bdId)}`)
      .then((result) => { if (!cancelled) setView(result); })
      .catch(() => { if (!cancelled) setView({ configured: false, reason: 'CDTT could not be read.', bdId, tokenAddress: null, providerAddress: null, wallet: null, balanceAtomic: '0', creditedAtomic: '0', totalSupplyAtomic: '0', tokenValueAtomic: '0', quoteCdttAtomic: '0', cdttPerBtcbAtomic: '0', multiplierBps: 0 }); });
    return () => { cancelled = true; };
  }, [bdId, refreshKey]);
  return view;
}

const cdttAmount = (atomic: string): string => {
  const value = Number(atomicToDecimal(atomic));
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} CDTT`;
};

export function PrimaryTokenCard({ token, actions, cdttRefreshKey = 0 }: { token: PrimaryTokenView; actions?: ReactNode; cdttRefreshKey?: number }) {
  const frozen = token.statusCode === 1;
  const cdtt = useCdttView(token.bdId, cdttRefreshKey);
  const tokenLiquidity = useTokenLiquidity();
  return (
    <section className="panel primary-token-card">
      <div className="panel-heading">
        <div><span className="eyebrow">PRIMARY TOKEN (RULE 14)</span><h2><TicketCheck size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />{token.tokenLabel}</h2></div>
        <span className={`token-status-badge${token.burned ? ' burned' : token.redeemed ? ' redeemed' : !token.minted ? ' pending' : ''}`}>
          {token.burned ? <Flame size={12} /> : frozen ? <Lock size={12} /> : <Unlock size={12} />} {token.status}
        </span>
      </div>
      {!token.identityValid ? (
        <p className="token-identity-error"><AlertTriangle size={14} /> Identity mismatch: on-chain token #{token.tokenId} does not equal {token.bdId}. Token operations are invalid until corrected (point 292).</p>
      ) : null}
      <dl className="primary-token-grid">
        <div><dt>Token ID</dt><dd>{token.tokenLabel}{token.minted ? <CheckCircle2 size={12} className="ok" /> : null}</dd></div>
        <div><dt>B-Dynamic ID</dt><dd>{token.bdId}</dd></div>
        <div><dt>Issue Era</dt><dd>{token.issueEra}</dd></div>
        <div><dt>Original ratio</dt><dd>{token.originalRatio}</dd></div>
        <div><dt>Current ratio</dt><dd>{token.burned || token.redeemed ? '—' : token.currentRatio ?? '—'}</dd></div>
        <div><dt>Burn progress</dt><dd>{token.minted ? burnProgress(ratioToBps(token.originalRatio), ratioToBps(token.currentRatio), token.burned) : '—'}</dd></div>
        <div><dt>Token value today (liquidity fund share)</dt><dd>{token.minted && !token.burned && !token.redeemed ? btcValue(tokenLiquidity?.valueFor(ratioToBps(token.currentRatio)) ?? null) : '—'}</dd></div>
        <div><dt>Freeze start</dt><dd>{token.freezeStartAt ? formatDate(token.freezeStartAt) : '—'}</dd></div>
        <div><dt>Unfreeze date</dt><dd>{token.unfreezeAt ? formatDate(token.unfreezeAt) : '365 days after minting'}</dd></div>
        <div><dt>Redemption</dt><dd>{token.redeemed ? 'Redeemed externally' : token.redemptionEligible ? 'Eligible' : 'Not eligible yet'}</dd></div>
        <div><dt>Burn status</dt><dd>{token.burned ? 'Burned' : 'Not burned'}</dd></div>
        <div><dt>Entry amount at mint (reference, not today's value)</dt><dd>{token.liquidityAtomic ? formatToken(atomicToDecimal(token.liquidityAtomic)) : '—'}</dd></div>
        <div><dt>Wallet</dt><dd>{token.wallet ? shortenAddress(token.wallet, 6) : '—'}</dd></div>
      </dl>
      <div className="cdtt-block">
        <div className="cdtt-head"><Coins size={14} /> <strong>CDTT · Community Discount Token</strong> <span>(rule 15 · one-click conversion after unfreeze, 2x-10x purchasing power)</span></div>
        {cdtt === null ? <p className="empty-copy">Reading CDTT from the contract…</p> : !cdtt.configured ? <p className="empty-copy">{cdtt.reason ?? 'CDTT is not configured.'}</p> : (
          <dl className="primary-token-grid cdtt-grid">
            <div><dt>CDTT balance ({token.bdId})</dt><dd>{cdttAmount(cdtt.balanceAtomic)}</dd></div>
            <div><dt>Credited via {token.tokenLabel}</dt><dd>{cdttAmount(cdtt.creditedAtomic)}</dd></div>
            <div><dt>Convert quote today</dt><dd>{!token.minted ? '— (not minted)' : token.burned ? '— (token burned, nothing to convert)' : token.redeemed ? '— (already redeemed)' : cdttAmount(cdtt.quoteCdttAtomic)}</dd></div>
            <div><dt>Live token value used for the quote</dt><dd>{!token.minted || token.burned || token.redeemed ? '—' : `${Number(atomicToDecimal(cdtt.tokenValueAtomic)).toFixed(10)} BTCB`}</dd></div>
            <div><dt>Multiplier</dt><dd>{cdtt.multiplierBps / 10_000}x</dd></div>
            <div><dt>Parity ({cdtt.parityLive ? 'live BTCB price' : 'admin-set BTCB price'})</dt><dd>{Number(atomicToDecimal(cdtt.cdttPerBtcbAtomic)).toLocaleString()} CDTT / BTCB</dd></div>
            {cdtt.cdtPerUsdAtomic && cdtt.cdtPerUsdAtomic !== '0' ? <div><dt>CDT unit (same in every country)</dt><dd>{Number(atomicToDecimal(cdtt.cdtPerUsdAtomic)).toLocaleString()} CDTT = 1 USD · BTCB at {(Number(atomicToDecimal(cdtt.cdttPerBtcbAtomic)) / Number(atomicToDecimal(cdtt.cdtPerUsdAtomic))).toLocaleString(undefined, { maximumFractionDigits: 2 })} USD</dd></div> : null}
            <div><dt>CDTT contract</dt><dd>{cdtt.tokenAddress ? shortenAddress(cdtt.tokenAddress, 6) : '—'}</dd></div>
          </dl>
        )}
      </div>
      {actions}
      <p className="empty-copy">
        {token.minted
          ? 'Read from the token contract. One B-Dynamic ID = one Primary Token; the token never replaces the ID and the ID stays permanent even after redemption or burn.'
          : 'Not minted yet. Under rule 14 the Primary Token is minted with the first eligible activation and takes this exact identity; automatic minting arrives with contract V2.'}
      </p>
    </section>
  );
}
